import React from 'react';
import { Tooltip } from '@cotera/client/app/components/ui';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { formatCredits } from '@cotera/client/v0/components/topbar/credit-balance-section';
import type { ChatCostTotals } from '@cotera/api';
import { useCocoChatModel } from '../models/coco-chat.model';
import { useAppModel } from '../../shell/app.model';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';

export const ChatCostBadge: React.FC = () => {
  const model = useCocoChatModel();
  const appModel = useAppModel();
  const totals = useWatchableValue<ChatCostTotals | null>(
    model.costTotalsWatchable
  );
  const { showCredits } = useWatchableValue(appModel.uiPreferences);

  if (!showCredits) {
    return null;
  }
  if (totals === null) {
    return null;
  }
  if (totals.credits === 0 && !totals.hasActiveFrame) {
    return null;
  }

  return (
    <Tooltip
      side="bottom"
      tooltipContent={
        <div className="flex flex-col gap-1 text-xs min-w-[140px]">
          <div className="flex justify-between gap-4">
            <span className="text-foreground/70">This chat</span>
            <span className="font-medium">
              {formatCredits(totals.credits)} cr
            </span>
          </div>
          {totals.breakdown.llm > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-foreground/70">LLM</span>
              <span>{formatCredits(totals.breakdown.llm)} cr</span>
            </div>
          )}
          {totals.breakdown.toolcall > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-foreground/70">Tools</span>
              <span>{formatCredits(totals.breakdown.toolcall)} cr</span>
            </div>
          )}
          {totals.hasActiveFrame && (
            <div className="mt-1 pt-1 border-t border-border text-foreground/60">
              Still running…
            </div>
          )}
        </div>
      }
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground/70 font-normal cursor-default select-none">
        <Icon icon="credits" />
        {/* `tabular-nums`: this number is rewritten on every frame of a running
            response, and in a proportional face each rewrite changes the string's
            width — so the badge crept sideways for the whole of a reply. Fixed
            digit widths hold it still for every update that keeps the same digit
            count, which is nearly all of them (`formatCredits` pins two decimals
            under 1,000). Crossing 10 or 100 still nudges it by one digit. */}
        <span
          className="tabular-nums"
          aria-label={`Chat cost: ${formatCredits(totals.credits)} credits`}
        >
          {formatCredits(totals.credits)} cr
        </span>
      </div>
    </Tooltip>
  );
};
